import { useContext, useState } from "react";
import { gql, useMutation } from "@apollo/client";
import Cta from "./CTA";
import { faCaretRight } from "@fortawesome/free-solid-svg-icons";
import { LINEITEM_STATUS } from "./constants";
import { MarketplaceGamDataContext } from "../../../context/marketplaceGamDataContext";

const DOMAIN_SELECTION_MUTATION = gql`
    mutation callMarketplaceNotificationMutation($from: String, $to: String, $subject: String, $action: String, $domainsToReview: String, $domainIds: String, $buyer: String, $campaignName: String, $campaignDetails: String) {
        marketplaceNotificationMutation(input: { from: $from, to: $to, subject: $subject, action: $action, domainsToReview: $domainsToReview, domainIds: $domainIds, buyer: $buyer, campaignName: $campaignName, campaignDetails: $campaignDetails }) {
            response
        }
    }
`;

/**
 * 
 * @param {Object} param0 
 * @param {Object.<string, DomainInformation>} param0.domainIdMappings
 * @param {MarketplaceOrder} param0.order
 * @param {Object.<string, number>} param0.domainToCustomerMappings
 * @param {function(boolean): Promise<[boolean, MarketplaceOrder, number[] | undefined]} param0.saveMarketplaceOrderHandler
 * @param {function(string[]): Promise<Object>} param0.requestLineItemStatsFn
 * @returns 
 */

const SaveSection = ({
    order,
    saveMarketplaceOrderHandler,
    selectSizesHandler,
    switchTabHandler,
    customerName,
    domainIdMappings,
    setMarketplaceOrderStats,
    requestLineItemStatsFn
}) => {
    let { activeTab = 0 } = order;
    const { gamStats: marketplaceOrderStats }
        = useContext(MarketplaceGamDataContext)
    const [showDraftLoader, setShowDraftLoader] = useState(false);
    const [showPublishLoader, setShowPublishLoader] = useState(false);
    const [domainSelectionNotificationMutation] = useMutation(DOMAIN_SELECTION_MUTATION);
    const ctaStyles = "min-width: unset !important; width: auto !important; margin-right: 1em;"
    const lineItemId = order.lineItemId
    const isCampaignComplete = lineItemId in marketplaceOrderStats && marketplaceOrderStats[lineItemId].status === LINEITEM_STATUS.COMPLETED

    /**
     * 
     * @param {MarketplaceOrder} order 
     * @param {number[]} domainsToNotify
     * @returns {undefined | Promise}
     */
    async function sendNotificationEmail(order, domainsToNotify) {
        /* 
            If the list of domainsToNotify is empty due to none of the domains being wrapper domains, then do not notify.
        */
        if (!domainsToNotify.length) {
            return
        }

        const { 
            name: campaignName,
            description,
            startDate,
            endDate,
            impressionGoals
        } = order;
        const domainsToReview = domainsToNotify.map(domainId => domainIdMappings[domainId].domainName);


        const variables = {
            subject: "Marketplace Domain Selection",
            from: "no-reply@adgrid.io",
            to: "",
            domainsToReview: JSON.stringify(domainsToReview),
            domainIds: JSON.stringify(domainsToNotify),
            action: "SEND_NETWORK_SELECTION_EMAIL",
            buyer: customerName,
            campaignName: order.name,
            campaignDetails: JSON.stringify({
                "Campaign name": campaignName,
                "Campaign start date": new Date(startDate).toLocaleDateString(),
                "Campaign end date": new Date(endDate).toLocaleDateString(),
                "Campaign notes": description || "n/a",
                "Impression goal": Number(impressionGoals).toLocaleString(),
            }),
        };

        await domainSelectionNotificationMutation({
            variables,
        });
    }

    return (
        <div className="p-grid p-fluid">
            <div className={`p-col-12 p-sm-12 p-md-3 p-lg-3 p-mt-2 small-margin-top-0 small-padding-bottom-0`}></div>
            <div className="p-col-12 p-sm-12 p-md-9 p-lg-9 p-mt-3">
                <div className="flex">
                    {
                        order.format === 'display' && (
                            <Cta
                                cssStyles={ctaStyles}
                                onClick={() => selectSizesHandler()}
                                ctaText="Expected Creative Sizes"
                                themeClasses="marketplace-cta default outlined"
                                disabled={isCampaignComplete}
                            />
                        )
                    }
                    <Cta
                        cssStyles={ctaStyles}
                        onClick={async () => {
                            setShowDraftLoader(true);
                            await saveMarketplaceOrderHandler(true);
                            setShowDraftLoader(false);
                        }}
                        ctaText="Save as draft"
                        themeClasses="marketplace-cta default outlined"
                        showLoader={showDraftLoader}
                        disabled={showPublishLoader || isCampaignComplete}
                    />
                    <Cta
                        cssStyles={ctaStyles}
                        onClick={async () => {
                            try {
                                setShowPublishLoader(true);
                                let [succeeded, marketplaceOrder, domainsToNotify] = await saveMarketplaceOrderHandler(false);
                                setShowPublishLoader(false);
                                if (succeeded) {
                                    let response = await requestLineItemStatsFn([marketplaceOrder.lineItemId])
                                    let stats = response[marketplaceOrder.lineItemId]

                                    if (typeof stats === 'object') {
                                        setMarketplaceOrderStats({
                                            [marketplaceOrder.lineItemId]: stats
                                        })
                                    }

                                    await sendNotificationEmail(order, domainsToNotify || []);
                                }
                            } catch (err) {
                                setShowPublishLoader(false);
                            }
                        }}
                        ctaText="Publish"
                        themeClasses="marketplace-cta success outlined"
                        showLoader={showPublishLoader}
                        disabled={showDraftLoader || isCampaignComplete}
                    />
                    {
                        activeTab === 0 && (
                            <Cta
                                cssStyles={ctaStyles}
                                onClick={switchTabHandler}
                                ctaText="Next"
                                icon={faCaretRight}
                                iconPos="right"
                                themeClasses="marketplace-cta success outlined"
                                disabled={isCampaignComplete}
                            />
                        )
                    }
                </div>
            </div>
        </div>
    );
};

export default SaveSection;
